import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import HSTable from "../../components/global/Table";
import BudgetEditorView from '../../components/budgets/BudgetEditorView';
import { CalculationProvider } from '../../context/CalculationContext';
import { getAllBudgets, createBudget, deleteBudget, updateBudget, listAllBudgets } from '../../services/budgets/BudgetService';
import IconButton from '@mui/material/IconButton';
import { Add, Delete, Edit } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { ToastContainer } from "react-toastify";
import Notify from "../../components/global/Notify";
import { useDesignTokens } from "../../context/ThemeContext";
import "../../css/BudgetsPage.css";
const notify = (message, isSuccess) => {
  isSuccess ? Notify.success(message) : Notify.error(message);
};

const BudgetsPage = () => {
  const { t } = useTranslation();
  const tokens = useDesignTokens();

  // ======================================================================
  // TABLE CONFIGURATION
  // ======================================================================
  const columns = [
    { field: "projectInfo.propose", label: t("budgetForm.page.columns.propose"), sortable: true },
    { field: "projectInfo.projectCode", label: t("budgetForm.page.columns.projectCode"), sortable: true },
    { field: "projectInfo.location", label: t("budgetForm.page.columns.location"), sortable: true },
    { field: "projectInfo.enviroment", label: t("budgetForm.page.columns.environment"), paddingLeft: 10, sortable: true },
    { field: "createdAt", label: t("budgetForm.page.columns.createdAt"), sortable: true, render: (value) => (value !== undefined && value != null ? value.split("T")[0] : "") },
    { field: "projectInfo.minTemperature", label: t("budgetForm.page.columns.minTemperature"), paddingLeft: 10, sortable: true },
    { field: "technicalData.degassingCapacity", label: t("budgetForm.page.columns.degassingCapacity"), paddingLeft: 10, sortable: true },
    {
      field: "editAction",
      label: t("budgetForm.page.columns.edit"),
      render: (_, row) => (
        <IconButton onClick={() => editBudgetFunction(row)}>
          <Edit fontSize="medium" sx={{ color: "primary.main" }} />
        </IconButton>
      ),
    },
    {
      field: "deleteAction",
      label: t("budgetForm.page.columns.delete"),
      render: (_, row) => (
        <IconButton onClick={() => setBudgetToDelete(row)}>
          <Delete fontSize="medium" sx={{ color: tokens.danger }} />
        </IconButton>
      ),
    },
  ];

  // ======================================================================
  // STATE
  // ======================================================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [allBudgetsList, setAllBudgetsList] = useState([]);
  const [budgetToDelete, setBudgetToDelete] = useState(null);

  // ======================================================================
  // FETCH ALL BUDGETS (for import)
  // ======================================================================
  useEffect(() => {
    if (showEditor && !selectedBudget) {
      listAllBudgets().then(setAllBudgetsList).catch(() => { });
    }
  }, [showEditor, selectedBudget]);

  // ======================================================================
  // FETCH ALL BUDGETS
  // ======================================================================
  const getAllBudgetsFunction = useCallback(async () => {
    try {
      const res = await getAllBudgets(page, rowsPerPage, searchString);
      setData(res.content);
      setTotalElements(res.totalElements);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      notify(t('budgetForm.page.notify.fetchError'), false);
    }
  }, [page, rowsPerPage, searchString, t]);


  useEffect(() => {
    setLoading(true);
    if (!showEditor) {
      getAllBudgetsFunction();
    }
  }, [showEditor, getAllBudgetsFunction]);

  // ======================================================================
  // HANDLE SEARCH CHANGE
  // ======================================================================
  const onSearchChange = (search) => {
    setSearchString(search);
  };

  // ======================================================================
  // CREATE / EDIT BUDGET SUBMIT (Step 1)
  // ======================================================================
  const handleBudgetSubmit = async (formData) => {
    formData.language = formData.language === "Português" ? "PT" : "EN";
    let response = null;
    try {
      if (selectedBudget) {
        response = await updateBudget(selectedBudget.id, formData);
        notify(t('budgetForm.page.notify.updated'), true);
      } else {
        response = await createBudget(formData);
        setSelectedBudget(response);
        notify(t('budgetForm.page.notify.created'), true);
      }
      setActiveStep(1);
    } catch {
      notify(t('budgetForm.page.notify.submitError'), false);
    }
    return response;
  };

  // ======================================================================
  // DELETE BUDGET
  // ======================================================================
  const deleteBudgetFunction = async (budget) => {
    setLoading(true);
    try {
      await deleteBudget(budget.id);
      notify(t("budgetForm.page.notify.deleted"), true);
      await getAllBudgetsFunction();
    } catch (error) {
      notify(error.message, false);
      setLoading(false);
    }
  };

  const confirmDeleteBudget = async () => {
    const budget = budgetToDelete;
    setBudgetToDelete(null);
    await deleteBudgetFunction(budget);
  };

  // ======================================================================
  // EDIT BUDGET
  // ======================================================================
  const editBudgetFunction = (budget) => {
    setActiveStep(0);
    setSelectedBudget(budget);
    setShowEditor(true);
  };

  const closeBudgetEditor = () => {
    setShowEditor(false);
  };

  return (
    <>
      {!showEditor && (
        <div className="table-responsive" style={{ boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)' }}>
          <HSTable
            columns={columns}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            enableSearch={true}
            totalCount={totalElements}
            onRowsPerPageChange={(newRowsPerPage) => { setRowsPerPage(newRowsPerPage); setPage(0); }}
            onPageChange={setPage}
            onSearchChange={onSearchChange}
          />
        </div>
      )}

      <div style={{ margin: '0.5rem 0', display: 'flex', justifyContent: 'flex-end', cursor: "pointer" }}
        onClick={() => {
          if (!showEditor) {
            setActiveStep(0);
            setSelectedBudget(null);
            setShowEditor(true);
          }
        }}>

        {!showEditor &&
          <>
            <span className="form-span"> {t("budgetForm.page.createNew")}</span>
            <IconButton
              sx={{ backgroundColor: showEditor ? 'error.main' : 'primary.main', color: '#fff' }}
            >
              <Add />
            </IconButton>
          </>
        }
      </div>

      {showEditor && (
        <div style={{ marginBottom: '1rem', padding: '0.5rem', paddingTop: "0rem", borderRadius: '8px' }}>
          <CalculationProvider>
            <BudgetEditorView
              title={selectedBudget ? t("budgetForm.page.editTitle") : t("budgetForm.page.newTitle")}
              buttonTitle={selectedBudget ? t("budgetForm.page.updateButton") : t("budgetForm.page.generateButton")}
              onSubmit={handleBudgetSubmit}
              initialData={selectedBudget}
              budgetsForImport={allBudgetsList}
              activeStep={activeStep}
              onStepChange={setActiveStep}
              oncloseBudgetEditor={closeBudgetEditor}
            />
          </CalculationProvider>
        </div>
      )}

      <Dialog open={!!budgetToDelete} onClose={() => setBudgetToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("budgetForm.page.deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {budgetToDelete?.projectInfo?.propose
              ? t("budgetForm.page.deleteDialog.confirm", { name: budgetToDelete.projectInfo.propose })
              : t("budgetForm.page.deleteDialog.confirmNoName")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetToDelete(null)}>{t("budgetForm.page.deleteDialog.cancel")}</Button>
          <Button onClick={confirmDeleteBudget} variant="contained" color="error">
            {t("budgetForm.page.deleteDialog.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default BudgetsPage;